import { Checkbox, Select, Textarea, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { ContractLandInputSchema } from '../schemas';
import type { LandLookups } from '../../types/api';
import { CreatableLookupSelect } from './lease-lookup-select';

/**
 * Contract (Land Data) edit-form field registry. Mirrors
 * api/src/lib/contract-land-form-layout-schema.ts — the field-name list
 * below MUST stay in sync with that file's `CONTRACT_LAND_FORM_FIELD_NAMES`.
 *
 * This is a mechanical clone of lease-fields.tsx: same context-bag shape
 * (lookups / members / canAssignAnyReviewer / currentUserId), same reviewer
 * field behavior (reused directly from Lease, not reimplemented), same
 * helper-based descriptor builders. Contract has no fed_rights-equivalent
 * field and no numeric fields, so numberField isn't needed here.
 *
 * Context bag carries the seeded dropdown options (`useLandLookups()`,
 * `GET /land-data/lookups`) — fetched once by the caller (the contract form
 * drawer / layout editor preview) and threaded down. Fields never call the
 * query hook themselves: `desc.render` is invoked inline inside a `.map()`
 * in `FormLayoutRenderer`, which would break the Rules of Hooks.
 */

export const CONTRACT_LAND_FORM_FIELD_NAMES = [
  'contract_number',
  'contract_title',
  'contract_type',
  'prospect',
  'field',
  'reviewer_user_id',
  'party_1',
  'other_parties',
  'county',
  'state',
  'township',
  'range',
  'section',
  'block',
  'contract_date',
  'book',
  'page',
  'entry',
  'flag_for_issues',
  'issue_remark',
  'description',
  'depth_limitations',
  'depth_remarks',
  'royalty',
  'orri',
  'non_consent',
  'non_consent_penalty',
  'call_on_production',
  'special_pooling_provisions',
  'special_pooling_remarks',
  'gas_balancing',
  'joa_form_type',
  'change_of_operator',
  'remarks',
  'reviewer_notes',
  'change_remarks',
] as const;

export type ContractLandFormFieldName = (typeof CONTRACT_LAND_FORM_FIELD_NAMES)[number];

export interface ContractLandFormMemberOption {
  value: string;
  label: string;
}

export interface ContractLandFormContext {
  lookups: LandLookups;
  /** Current project's members, for the reviewer_user_id dropdown. */
  members: ContractLandFormMemberOption[];
  /** owner/superadmin may assign any member; moderator/editor are self-only. */
  canAssignAnyReviewer: boolean;
  currentUserId: string;
}

type ContractLandFieldDescriptor = FieldDescriptor<ContractLandInputSchema, ContractLandFormContext>;

function textProps(
  descriptor: { label: string; placeholder?: string },
  disabled: boolean,
  required = false,
) {
  const props: { label: string; placeholder?: string; disabled?: boolean; required?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  if (required) props.required = true;
  return props;
}

function textField(
  label: string,
  defaultSpan: number,
  opts: { placeholder?: string; required?: boolean } = {},
): ContractLandFieldDescriptor {
  const desc: ContractLandFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled, opts.required)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number): ContractLandFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
}

function dateField(label: string, defaultSpan: number): ContractLandFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DateInput
            label={descriptor.label}
            valueFormat="YYYY-MM-DD"
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            clearable
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function checkboxField(label: string, defaultSpan: number): ContractLandFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Checkbox
            label={descriptor.label}
            checked={(field.value as boolean | null) ?? false}
            onChange={(e) => field.onChange(e.currentTarget.checked)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function lookupSelectField(
  label: string,
  kind: keyof LandLookups,
  defaultSpan: number,
): ContractLandFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, context, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <CreatableLookupSelect
            label={descriptor.label}
            options={context.lookups[kind]}
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

/**
 * Reviewer dropdown: a Select of the current project's members. Behavior is
 * identical to lease-fields.tsx's `reviewerField` (the change-only authz
 * lives server-side in api/src/routes/contracts-land.ts's
 * assertReviewerAssignable) — reused wholesale rather than reimplemented.
 * For moderator/editor callers (`context.canAssignAnyReviewer === false`)
 * the option list is restricted to self-only, but the CURRENT value must
 * still be visible and round-trip unchanged: if it belongs to someone else
 * the Select renders disabled (visible, not reassignable); if it's already
 * self (or empty) the normal self-only picker applies.
 */
const reviewerField: ContractLandFieldDescriptor = {
  label: 'Reviewer',
  defaultSpan: 4,
  render: ({ name, control, disabled, context }) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const currentValue = (field.value as string | null) ?? null;
          const isOtherReviewer =
            !context.canAssignAnyReviewer &&
            currentValue !== null &&
            currentValue !== context.currentUserId;
          const options = context.canAssignAnyReviewer
            ? context.members
            : context.members.filter((m) => m.value === context.currentUserId);
          if (isOtherReviewer && !options.some((m) => m.value === currentValue)) {
            const known = context.members.find((m) => m.value === currentValue);
            options.push({ value: currentValue, label: known?.label ?? currentValue });
          }
          return (
            <Select
              label="Reviewer"
              data={options}
              searchable
              clearable
              value={currentValue}
              onChange={(v) => field.onChange(v ?? null)}
              disabled={disabled || isOtherReviewer}
            />
          );
        }}
      />
    );
  },
};

export const CONTRACT_LAND_FORM_FIELDS: FieldRegistry<
  ContractLandInputSchema,
  ContractLandFormFieldName,
  ContractLandFormContext
> = {
  contract_number: textField('Contract number', 6, { required: true }),
  contract_title: textField('Contract title', 6),
  contract_type: lookupSelectField('Type', 'contract_type', 4),
  prospect: textField('Prospect', 4),
  field: textField('Field', 4),
  reviewer_user_id: reviewerField,

  party_1: textField('Party 1', 6),
  other_parties: textField('Other parties', 6),

  county: textField('County', 6),
  state: textField('State', 6),
  township: textField('Township', 3),
  range: textField('Range', 3),
  section: textField('Section', 3),
  block: textField('Block', 3),

  contract_date: dateField('Date', 4),

  book: textField('Book', 4),
  page: textField('Page', 4),
  entry: textField('Entry', 4),

  flag_for_issues: lookupSelectField('Flag for issues', 'flag_for_issues', 4),
  issue_remark: textareaField('Issue remark', 12),

  description: textareaField('Description', 12),

  depth_limitations: checkboxField('Depth limitations', 3),
  depth_remarks: textareaField('Depth remarks', 12),
  royalty: checkboxField('Royalty', 3),
  orri: checkboxField('ORRI', 3),
  non_consent: checkboxField('Non-consent', 3),
  non_consent_penalty: textField('Non-consent penalty', 6),
  call_on_production: checkboxField('Call on production', 3),
  special_pooling_provisions: checkboxField('Special pooling provisions', 3),
  special_pooling_remarks: textareaField('Special pooling remarks', 12),
  gas_balancing: checkboxField('Gas balancing (GBA)', 3),

  joa_form_type: textField('JOA form type', 6),
  change_of_operator: textField('Change of operator info', 6),

  remarks: textareaField('Remarks', 12),
  reviewer_notes: textareaField('Reviewer notes', 12),
  change_remarks: textareaField('Change remarks', 12),
};

/**
 * Built-in default. Groups mirror
 * api/src/lib/contract-land-form-layout-schema.ts's `DEFAULT_LAYOUT`
 * (identity / parties / PLSS+location / dates / recording / issue tracking
 * / description / flags / contract administration / notes) so a
 * freshly-deployed DB renders sensibly before any admin save.
 */
export const CONTRACT_LAND_DEFAULT_LAYOUT: LayoutDocument<ContractLandFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'identity',
      title: 'Identity',
      fields: [
        { name: 'contract_number', span: 6 },
        { name: 'contract_title', span: 6 },
        { name: 'contract_type', span: 4 },
        { name: 'prospect', span: 4 },
        { name: 'field', span: 4 },
        { name: 'reviewer_user_id', span: 4 },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      fields: [
        { name: 'party_1', span: 6 },
        { name: 'other_parties', span: 6 },
      ],
    },
    {
      id: 'plss_location',
      title: 'PLSS / location',
      fields: [
        { name: 'county', span: 6 },
        { name: 'state', span: 6 },
        { name: 'township', span: 3 },
        { name: 'range', span: 3 },
        { name: 'section', span: 3 },
        { name: 'block', span: 3 },
      ],
    },
    {
      id: 'dates',
      title: 'Dates',
      fields: [{ name: 'contract_date', span: 4 }],
    },
    {
      id: 'recording',
      title: 'Recording',
      fields: [
        { name: 'book', span: 4 },
        { name: 'page', span: 4 },
        { name: 'entry', span: 4 },
      ],
    },
    {
      id: 'issue_tracking',
      title: 'Issue tracking',
      fields: [
        { name: 'flag_for_issues', span: 4 },
        { name: 'issue_remark', span: 12 },
      ],
    },
    {
      id: 'description',
      title: 'Description',
      fields: [{ name: 'description', span: 12 }],
    },
    {
      id: 'flags',
      title: 'Flags',
      fields: [
        { name: 'depth_limitations', span: 3 },
        { name: 'depth_remarks', span: 12 },
        { name: 'royalty', span: 3 },
        { name: 'orri', span: 3 },
        { name: 'non_consent', span: 3 },
        { name: 'non_consent_penalty', span: 6 },
        { name: 'call_on_production', span: 3 },
        { name: 'special_pooling_provisions', span: 3 },
        { name: 'special_pooling_remarks', span: 12 },
        { name: 'gas_balancing', span: 3 },
      ],
    },
    {
      id: 'administration',
      title: 'Contract administration',
      fields: [
        { name: 'joa_form_type', span: 6 },
        { name: 'change_of_operator', span: 6 },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      fields: [
        { name: 'remarks', span: 12 },
        { name: 'reviewer_notes', span: 12 },
        { name: 'change_remarks', span: 12 },
      ],
    },
  ],
};
